import type { TradingSessionStatus } from '@/lib/api/tradingSession';

const COARSE_TICK_MS = 30_000;
const PRECISE_TICK_MS = 1_000;
const PRECISE_WINDOW_MS = 90_000;

export function getTradingSessionTickerDelay(
    status: TradingSessionStatus | null,
    serverNowMs: number,
    isVisible: boolean,
): number | null {
    if (
        !isVisible ||
        status?.state !== 'active' ||
        (status.method !== 'eip712' && status.method !== 'passkey') ||
        typeof status.expiresAtMs !== 'number'
    ) {
        return null;
    }

    const remainingMs = status.expiresAtMs - serverNowMs;
    if (remainingMs <= 0) return null;
    return remainingMs <= PRECISE_WINDOW_MS
        ? PRECISE_TICK_MS
        : COARSE_TICK_MS;
}

/**
 * Longest a proactive OAuth refresh may be deferred, and the shortest gap
 * between two of them. The first keeps the client copy from ever reaching the
 * expiry it was told about; the second bounds the polling if the server keeps
 * handing back the same expiry.
 */
const OAUTH_REFRESH_LEAD_MS = 120_000;
const OAUTH_REFRESH_MIN_DELAY_MS = 15_000;
const OAUTH_REFRESH_LIFETIME_FRACTION = 0.2;

/**
 * When to re-read the trading-session status of an OAuth-backed authority.
 *
 * Unlike a signed window, this one is not a thing the user opened and can see
 * counting down: it expires with the provider token, and the server refreshes
 * that token silently. Nothing in the UI would notice, so the client copy has
 * to be renewed on its own, at 80% of the lifetime and never later than two
 * minutes before the expiry it currently believes in.
 *
 * Null when there is nothing to keep warm (no OAuth authority, or one that has
 * already lapsed — the mutation preflight refreshes that case on demand).
 */
export function getOAuthAuthorityRefreshDelay(
    status: TradingSessionStatus | null,
    serverNowMs: number,
): number | null {
    if (status?.state !== 'active' || status.method !== 'oauth') return null;
    if (typeof status.expiresAtMs !== 'number') return null;

    const remainingMs = status.expiresAtMs - serverNowMs;
    if (remainingMs <= 0) return null;

    const lifetimeMs =
        typeof status.issuedAtMs === 'number'
            ? Math.max(0, status.expiresAtMs - status.issuedAtMs)
            : remainingMs;
    const leadMs = Math.min(
        Math.max(
            lifetimeMs * OAUTH_REFRESH_LIFETIME_FRACTION,
            OAUTH_REFRESH_LEAD_MS,
        ),
        remainingMs / 2,
    );
    return Math.max(remainingMs - leadMs, OAUTH_REFRESH_MIN_DELAY_MS);
}
